import {
  AUDIENCE_DRAFT_POLL_MS,
  AUDIENCE_DRAFT_WAIT_MS,
  AUDIENCE_LANGUAGE,
  type AudienceContent,
  type AudienceScreener,
  type DraftArtifactKind,
  DG_NOT_APPROVED_REFUSAL,
  MAX_SCREENER_OPTION_CHARS,
  MAX_SCREENER_TEXT_CHARS,
  QUALITY_FLOOR_REFUSAL,
  type ReconciliationItem,
  audienceDraftRequestNeeded,
  canonicalJson,
  countryNamesInReadingOrder,
  sameAudienceDraftInputs,
  stalenessWindowsOf,
} from '@sigora/domain';
import { Fragment, type KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';

import {
  type AudienceCatalog,
  type AudienceView,
  approveArtifact,
  fetchAudience,
  requestAudienceDraft,
  rewordAudienceScreener,
} from '../api/client.ts';
import { domainCodeOf } from '../api/errors.ts';
import { type AudienceLabel, labelAudienceFilter } from '../artifacts/audience-labels.ts';
import { STATUS_LABELS } from '../artifacts/state.ts';
import {
  AUDIENCE_BEFORE_CHANGE,
  AUDIENCE_MARKS,
  AUDIENCE_NEEDS_GUIDE,
  AUDIENCE_REMOVED_HEADING,
  AUDIENCE_UPDATE_FAILED,
  AUDIENCE_UPDATE_OVERDUE,
  audienceUnchangedNotice,
  audienceUpdatedNotice,
  audienceUpdatingNotice,
} from './audience-copy.ts';
import { useRequestWait } from './use-request-wait.ts';
import { Pending, PendingMark } from '../ui/Pending.tsx';
import {
  APPROVING_LABEL,
  AUDIENCE_DRAFT_FAILED_HINT,
  AUDIENCE_DRAFTING_HINT,
  AUDIENCE_RETRY_HINT,
  AUDIENCE_UPDATING_HINT,
  EDITS_OPEN_HINT,
  SAVING_HINT,
} from './continue-copy.ts';
import { type FirstDraft, firstDraftOf } from './first-draft.ts';
import { useRefusalFocus } from './refusal-focus.ts';
import { useContinueAction } from './step-footer.tsx';
import { VersionHistory, historyEntriesOf } from './VersionHistory.tsx';

export interface AudienceStepProps {
  studyId: string;
  /** The study's Study Type as the recommendation names it, or null while none is chosen. */
  studyTypeName: string | null;
  /**
   * Whether the study's inputs are fixed: paid for, frozen or launched. The step is then shown as
   * it stands, offering no edit or approval the server would refuse.
   */
  frozen: boolean;
  /** The panel's demographics, for naming the filters; null while unread or unreadable. */
  audienceCatalog: AudienceCatalog | null;
  /** Bumped by the shell whenever the study may have changed; the step is read again on it. */
  refreshToken: number;
  /** Tells the shell the audience changed, so the steps and the sheet are read again. */
  onStudyChanged: () => void;
  /** Hands the shell the turn an approval started. */
  onApproved: (approved: { kind: DraftArtifactKind; turnId: string | null }) => void;
}

/** One open edit: a screening question's wording and its options' wording, as typed so far. */
export interface ScreenerEdit {
  text: string;
  options: readonly { id: string; text: string }[];
}

/** What the step says when what it showed moved under an edit or an approval. */
const CHANGED_NOTICE = 'This audience changed; look again.';

/**
 * The refusals that mean the audience on screen is no longer the one the server holds: a version
 * that moved, or a change above it that landed after the render.
 */
const MOVED_CODES = new Set(['artifact_version_not_current', 'artifact_stale']);

/** What an approval refusal says, by its code. */
const APPROVE_REFUSALS: Record<string, string> = {
  dg_not_approved: DG_NOT_APPROVED_REFUSAL,
  audience_below_quality_floor: QUALITY_FLOOR_REFUSAL,
  audience_catalog_not_configured:
    "This environment can't check audiences against the panel, so nothing can be approved here.",
  audience_catalog_not_entitled:
    "This workspace can't recruit participants yet, so the audience can't be checked.",
};

/** How each kind of Limitation is labelled when the list is open. */
const LIMITATION_KIND_LABELS = {
  targeting_gap: "Can't be targeted",
  pipeline_constraint: 'How this study runs',
} as const;

const failureMessage = (failure: unknown, fallback: string): string =>
  failure instanceof Error ? failure.message : fallback;

/** A ref callback that keeps a mounted element in `map` under `id`, and drops it on unmount. */
const refInto =
  <Element,>(map: Map<string, Element>, id: string) =>
  (element: Element | null) => {
    if (element === null) {
      map.delete(id);
    } else {
      map.set(id, element);
    }
  };

/** An edit can be saved once the question and every one of its options say something. */
const savable = (edit: ScreenerEdit) =>
  edit.text.trim() !== '' && edit.options.every((option) => option.text.trim() !== '');

/**
 * The step as a landed reword leaves it, before the read that confirms it: the screener in the
 * researcher's words, its options' text matched by id, marked as theirs, at the version the reword
 * wrote and unapproved. Nothing else moves, the Limitations included.
 */
export const applyScreenerReword = (
  view: AudienceView,
  screenerId: string,
  edit: ScreenerEdit,
  version: number,
): AudienceView => {
  if (view.audience === null) {
    return view;
  }

  const typed = new Map(edit.options.map((option) => [option.id, option.text]));
  const content: AudienceContent = {
    ...view.audience.content,
    screeners: view.audience.content.screeners.map((held) =>
      held.id !== screenerId
        ? held
        : {
            ...held,
            text: edit.text,
            options: held.options.map((option) => ({
              ...option,
              text: typed.get(option.id) ?? option.text,
            })),
            worded_by: 'user' as const,
          },
    ),
  };

  return {
    ...view,
    audience: { ...view.audience, version, status: 'unapproved', content },
    // The version on screen is the researcher's rewording now, not a reconciled one.
    reconciliation: null,
  };
};

/**
 * The open edits over the screeners an audience now holds. An edit keeps everything typed while its
 * screener still has the options it was opened on; one whose options were recast in the step's
 * conversation is rebuilt over the new ones — the question as typed, each option still there as
 * typed and each new one as it now reads — because a reword quotes option ids, and the old ones
 * would be refused on every save. An edit of a screener the audience no longer holds is dropped.
 * `changed` names every edit rebuilt or dropped.
 */
export const reconcileScreenerEdits = (
  edits: ReadonlyMap<string, ScreenerEdit>,
  screeners: readonly AudienceScreener[],
): { edits: ReadonlyMap<string, ScreenerEdit>; changed: ReadonlySet<string> } => {
  const held = new Map(screeners.map((screener) => [screener.id, screener]));
  const changed = new Set<string>();
  const next = new Map<string, ScreenerEdit>();

  for (const [id, edit] of edits) {
    const screener = held.get(id);
    if (screener === undefined) {
      changed.add(id);
      continue;
    }

    const sameOptions =
      screener.options.length === edit.options.length &&
      screener.options.every((option, index) => option.id === edit.options[index]?.id);
    if (sameOptions) {
      next.set(id, edit);
      continue;
    }

    changed.add(id);
    const typed = new Map(edit.options.map((option) => [option.id, option.text]));
    next.set(id, {
      text: edit.text,
      options: screener.options.map((option) => ({
        id: option.id,
        text: typed.get(option.id) ?? option.text,
      })),
    });
  }

  return { edits: changed.size === 0 ? edits : next, changed };
};

/** One Limitation as the step lists it: its text, and which kind it is. */
interface ShownLimitation {
  key: string;
  text: string;
  kind: keyof typeof LIMITATION_KIND_LABELS;
}

/** Every Limitation in stored order: Sigora's targeting gaps, then what the pipeline imposes. */
const limitationsOf = (content: AudienceContent): ShownLimitation[] => [
  ...content.limitations.targeting_gaps.map((gap) => ({
    key: gap.id,
    text: gap.text,
    kind: 'targeting_gap' as const,
  })),
  ...content.limitations.pipeline_constraints.map((constraint) => ({
    key: constraint.key,
    text: constraint.text,
    kind: 'pipeline_constraint' as const,
  })),
];

/**
 * What an update changed or added on one item, with the reason Sigora gave: shown on an updated
 * audience until it is approved. An item Sigora gave no reason for is explained by why the version
 * was made (its History line), shown beside the mark, so no change shows unexplained.
 */
const ChangeMark = ({
  item,
  explained,
}: {
  item: ReconciliationItem | undefined;
  explained: string;
}) => {
  if (item === undefined || item.change === 'removed') {
    return null;
  }
  const reason = item.reason ?? explained;
  return (
    <>
      {' '}
      <span className="guide-tag" data-test-id="audience-mark" data-change={item.change}>
        {AUDIENCE_MARKS[item.change]}
        {reason === '' ? '' : `: ${reason}`}
      </span>
    </>
  );
};

/**
 * What an update changed in a field of the audience itself — who, how many, where — which has no
 * reason of its own: why the version was made (its History line), which says what the update
 * answered, explains it.
 */
const FieldMark = ({ shown, explained }: { shown: boolean; explained: string }) =>
  shown ? (
    <>
      {' '}
      <span className="guide-tag" data-test-id="audience-mark" data-change="changed">
        {AUDIENCE_MARKS.changed}
        {explained === '' ? '' : `: ${explained}`}
      </span>
    </>
  ) : null;

/** A demographic or a choice in the panel's words, muted where the panel does not list it. */
const AudienceValue = ({ label }: { label: AudienceLabel }) =>
  label.unrecognised ? (
    <span className="audience-unlabelled" data-test-id="audience-unlabelled">
      {label.text}
    </span>
  ) : (
    <span>{label.text}</span>
  );

/**
 * The Limitations: the first in full and how many more, opened to the whole list with each one's
 * kind.
 */
const Limitations = ({
  content,
  marks,
  explained,
}: {
  content: AudienceContent;
  /** What an update changed or added, by item id; see `ChangeMark`. */
  marks: ReadonlyMap<string, ReconciliationItem>;
  /** Why the version was made (its History line), which explains a mark Sigora gave no reason for. */
  explained: string;
}) => {
  const [open, setOpen] = useState(false);
  const all = limitationsOf(content);
  const first = all[0];
  if (first === undefined) {
    return null;
  }

  const more = all.length - 1;

  return (
    <div className="audience-limitations" data-test-id="audience-limitations">
      {open ? (
        <ul className="audience-limitation-list" id="audience-limitation-list">
          {all.map((limitation) => (
            <li
              key={limitation.key}
              data-test-id="audience-limitation"
              data-limitation-kind={limitation.kind}
            >
              <span className="audience-limitation-kind">
                {LIMITATION_KIND_LABELS[limitation.kind]}:
              </span>{' '}
              {limitation.text}
              <ChangeMark item={marks.get(limitation.key)} explained={explained} />
            </li>
          ))}
        </ul>
      ) : (
        <p data-test-id="audience-limitations-summary">
          Limitation: {first.text}
          {more > 0 && ` and ${String(more)} more`}
        </p>
      )}
      {more > 0 && (
        <button
          type="button"
          data-test-id="audience-limitations-toggle"
          aria-expanded={open}
          aria-controls="audience-limitation-list"
          onClick={() => setOpen((shown) => !shown)}
        >
          {open ? 'Show fewer' : 'Show all'}
        </button>
      )}
    </div>
  );
};

/**
 * The Audience step: who Sigora will find and how many, where they live and the language they take
 * part in, the panel filters, the screening questions with the answers that end the survey, the
 * Limitations. The footer's Continue approves it.
 *
 * Everything shown is the server's: the audience, every version with what produced it, the count
 * the Study Type recommends and the Quality floor. A reword and an approval both name the version
 * on screen, and the server refuses either over an audience that moved.
 *
 * An audience waiting on a change above it is updated on arrival: the step asks once per set of
 * inputs, shows the version before the change under a label while it waits, and says when the
 * update failed, with "Try again". It asks nothing while the guide is not approved, and says so. An
 * updated audience says what it was updated for, marks each item it changed or added with the
 * reason given, and lists what it took out, until it is approved.
 *
 * The researcher changes the wording of one screening question at a time — its text and its
 * options' text, never their number, order or which of them end the survey. The count, the
 * countries, the filters, the screeners themselves and the Limitations change only through the
 * step's conversation, whose turns end with the step read again.
 */
export const AudienceStep = ({
  studyId,
  studyTypeName,
  frozen,
  audienceCatalog,
  refreshToken,
  onStudyChanged,
  onApproved,
}: AudienceStepProps) => {
  const [view, setView] = useState<AudienceView | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  /** Why the step was read again under the reader, said once over it. */
  const [notice, setNotice] = useState<string | null>(null);
  const [approving, setApproving] = useState(false);
  const [approveError, setApproveError] = useState<string | null>(null);
  /** Every open edit, by screener id. */
  const [edits, setEdits] = useState<ReadonlyMap<string, ScreenerEdit>>(new Map());
  /** The screener whose reword is out. */
  const [saving, setSaving] = useState<string | null>(null);
  /**
   * A reword or an approval has landed and the read that follows it has not. Every save and the
   * approval wait: the audience on screen is the one the write replaced.
   */
  const [settling, setSettling] = useState(false);
  /** The first read that can settle a write: the one it started, or any newer one. */
  const settlesFrom = useRef<number | null>(null);
  /** What the server refused a reword with, on the screener it was made on. */
  const [editErrors, setEditErrors] = useState<ReadonlyMap<string, string>>(new Map());
  /** A request for an update is out. */
  const [asking, setAsking] = useState(false);
  /** Why the last request for an update could not be made. */
  const [requestError, setRequestError] = useState<string | null>(null);

  /** Only the newest read is shown, so an older one landing last cannot put back what moved. */
  const latest = useRef(0);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;

    return () => {
      alive.current = false;
    };
  }, []);

  const editButtons = useRef(new Map<string, HTMLButtonElement>());
  const editFields = useRef(new Map<string, HTMLTextAreaElement>());
  /** The screener an edit was just opened on, whose field takes the focus. */
  const [focusField, setFocusField] = useState<string | null>(null);
  /** The screener an edit was just closed on, whose Edit button takes the focus back. */
  const [returnFocusTo, setReturnFocusTo] = useState<string | null>(null);
  /** The version the step last showed; an approval refused over it says nothing about the next. */
  const shownVersion = useRef<number | null>(null);

  /** Something is in flight that a reword or an approval must not race. */
  const busy = approving || saving !== null || settling || asking;

  const load = useCallback(
    async (signal?: AbortSignal) => {
      latest.current += 1;
      const ticket = latest.current;
      const settle = () => {
        if (settlesFrom.current !== null && ticket >= settlesFrom.current) {
          settlesFrom.current = null;
          setSettling(false);
        }
      };

      try {
        const next = await fetchAudience(studyId, signal);
        if (alive.current && ticket === latest.current) {
          setView(next);
          setLoadError(null);
          const version = next.audience?.version ?? null;
          if (shownVersion.current !== null && shownVersion.current !== version) {
            setApproveError(null);
          }
          shownVersion.current = version;
          settle();
        }
      } catch (failure: unknown) {
        if (alive.current && ticket === latest.current && signal?.aborted !== true) {
          setLoadError(failureMessage(failure, 'Your audience could not be read.'));
          settle();
        }
      }
    },
    [studyId],
  );

  useEffect(() => {
    void load();
  }, [load, refreshToken]);

  /** Holds the step after a write until a read started now, or a newer one, has landed. */
  const holdUntilRead = () => {
    setSettling(true);
    settlesFrom.current = latest.current + 1;
    void load();
  };

  useEffect(() => {
    if (focusField !== null) {
      editFields.current.get(focusField)?.focus();
      setFocusField(null);
    }
  }, [focusField]);

  useEffect(() => {
    if (returnFocusTo !== null && !edits.has(returnFocusTo)) {
      editButtons.current.get(returnFocusTo)?.focus();
      setReturnFocusTo(null);
    }
  }, [returnFocusTo, edits]);

  /**
   * A read that brings a screener with different options, or none, under an open edit: the edit is
   * rebuilt over the screener as it now stands, or dropped with it, and the step says so once.
   */
  useEffect(() => {
    const screeners = view?.audience?.content.screeners;
    if (screeners === undefined || edits.size === 0) {
      return;
    }

    const reconciled = reconcileScreenerEdits(edits, screeners);
    if (reconciled.changed.size === 0) {
      return;
    }

    setEdits(reconciled.edits);
    setEditErrors((errors) => {
      const next = new Map(errors);
      for (const id of reconciled.changed) {
        next.delete(id);
      }

      return next;
    });
    setNotice(CHANGED_NOTICE);
  }, [view, edits]);

  const setEditError = (screenerId: string, message: string | null) => {
    setEditErrors((errors) => {
      const next = new Map(errors);
      if (message === null) {
        next.delete(screenerId);
      } else {
        next.set(screenerId, message);
      }

      return next;
    });
  };

  const closeEdit = (screenerId: string) => {
    setEdits((open) => {
      const next = new Map(open);
      next.delete(screenerId);

      return next;
    });
    setEditError(screenerId, null);
    setReturnFocusTo(screenerId);
  };

  const openEdit = (screener: AudienceScreener) => {
    setEdits((open) =>
      new Map(open).set(screener.id, {
        text: screener.text,
        options: screener.options.map((option) => ({ id: option.id, text: option.text })),
      }),
    );
    setEditError(screener.id, null);
    setFocusField(screener.id);
  };

  const typeInto = (screenerId: string, change: (edit: ScreenerEdit) => ScreenerEdit) => {
    setEdits((open) => {
      const edit = open.get(screenerId);

      return edit === undefined ? open : new Map(open).set(screenerId, change(edit));
    });
  };

  /** A refusal that says the audience on screen moved: say so once, and read it again. */
  const moved = () => {
    setNotice(CHANGED_NOTICE);
    void load();
    onStudyChanged();
  };

  /**
   * Whether a refusal says the audience on screen moved. When it does, the step has said so and is
   * reading it again, and the caller does nothing more.
   */
  const answeredMove = (code: string | null): boolean => {
    if (code === null || !MOVED_CODES.has(code)) {
      return false;
    }

    moved();
    return true;
  };

  /** A write that landed: the step holds until a read lands, and the shell reads the study again. */
  const landed = () => {
    holdUntilRead();
    onStudyChanged();
  };

  const audience = view?.audience ?? null;

  /**
   * Asks for the audience to be updated, shows the request the server stored, and then reads the
   * whole step again, which is what makes the answer stick: a read started while the request was in
   * flight can have looked before it committed and still land last.
   */
  const askDraft = useCallback(
    (again: boolean) => {
      setAsking(true);
      setRequestError(null);
      latest.current += 1;
      const ticket = latest.current;

      void requestAudienceDraft(studyId, { again }).then(
        (draft) => {
          setAsking(false);
          if (alive.current && ticket === latest.current && draft !== null) {
            setView((current) => (current === null ? current : { ...current, draft }));
          }
          void load();
        },
        (failure: unknown) => {
          setAsking(false);
          if (alive.current) {
            setRequestError(
              failureMessage(failure, 'Sigora could not be asked to update the audience.'),
            );
          }
          void load();
        },
      );
    },
    [studyId, load],
  );

  const currentInputs = view?.currentInputs ?? null;
  const draft = view?.draft ?? null;
  /** The latest request, while it is for the study as it stands. */
  const draftForNow =
    draft !== null && currentInputs !== null && sameAudienceDraftInputs(draft.inputs, currentInputs)
      ? draft
      : null;
  const draftPendingId = draftForNow?.status === 'pending' ? draftForNow.id : null;
  const draftOverdue = useRequestWait({
    pendingId: draftPendingId,
    pollMs: AUDIENCE_DRAFT_POLL_MS,
    waitMs: AUDIENCE_DRAFT_WAIT_MS,
    load,
    alive,
  });

  /**
   * The first audience, while the guide is approved and none is written yet: it is drafted by the
   * turn the guide's approval starts on the Guide thread, which this step cannot watch, so the step
   * is read again on the draft's own cadence until it lands or the draft's wait runs out. The
   * thread on screen reports that turn ending too; this covers a report that never comes. Once the
   * wait runs out with no audience, the first draft is taken to have failed.
   */
  const firstDraftId =
    view !== null && view.audience === null && view.guideApproved && !frozen
      ? `first-audience:${studyId}`
      : null;
  const firstDraftOverdue = useRequestWait({
    pendingId: firstDraftId,
    pollMs: AUDIENCE_DRAFT_POLL_MS,
    waitMs: AUDIENCE_DRAFT_WAIT_MS,
    load,
    alive,
  });

  /**
   * Arrival: ask once for the study's inputs when the audience is waiting on a change and no
   * request for those inputs is still worth waiting on. Keyed on the inputs, so a change above it
   * landing while the step is open asks again, and a request that failed is not repeated on every
   * read; "Try again" is how it is asked for again.
   */
  const askedFor = useRef<string | null>(null);
  useEffect(() => {
    if (view === null || view.audience === null || currentInputs === null || frozen) {
      return;
    }

    const key = canonicalJson(currentInputs);
    if (askedFor.current === key) {
      return;
    }

    askedFor.current = key;
    if (
      audienceDraftRequestNeeded({
        request: view.draft,
        inputs: currentInputs,
        audience: { staleness: stalenessWindowsOf(view.audience.staleReason) },
        now: new Date(),
      })
    ) {
      askDraft(false);
    }
  }, [view, currentInputs, frozen, askDraft]);

  const save = (screener: AudienceScreener) => {
    const edit = edits.get(screener.id);
    if (audience === null || edit === undefined || busy || !savable(edit)) {
      return;
    }

    setSaving(screener.id);
    setEditError(screener.id, null);
    setNotice(null);

    void rewordAudienceScreener({
      studyId,
      screenerId: screener.id,
      text: edit.text,
      options: edit.options,
      expectedVersion: audience.version,
    }).then(
      ({ version, deduped }) => {
        setSaving(null);
        setApproveError(null);
        closeEdit(screener.id);
        if (!deduped) {
          setView((current) =>
            current === null ? current : applyScreenerReword(current, screener.id, edit, version),
          );
        }
        landed();
      },
      (failure: unknown) => {
        setSaving(null);
        const code = domainCodeOf(failure);

        // What was typed stays open over the audience as it is now, and saving it again names it.
        if (answeredMove(code)) {
          return;
        }

        if (code === 'audience_screener_not_found') {
          closeEdit(screener.id);
          moved();
          return;
        }

        setEditError(screener.id, failureMessage(failure, 'That wording could not be saved.'));
      },
    );
  };

  /** Approves the audience on screen, from Continue; true once accepted. */
  const approve = (): Promise<boolean> => {
    if (audience === null || busy) {
      return Promise.resolve(false);
    }

    setApproving(true);
    setApproveError(null);
    setNotice(null);

    return approveArtifact({
      studyId,
      artifactId: audience.artifactId,
      version: audience.version,
    }).then(
      ({ turnId }) => {
        setApproving(false);
        onApproved({ kind: 'audience', turnId });
        landed();
        return true;
      },
      (failure: unknown) => {
        setApproving(false);
        const code = domainCodeOf(failure);

        if (answeredMove(code)) {
          return false;
        }

        setApproveError(
          (code === null ? undefined : APPROVE_REFUSALS[code]) ??
            failureMessage(failure, 'This audience could not be approved.'),
        );
        if (code !== null) {
          void load();
        }
        return false;
      },
    );
  };

  const stale = audience !== null && (audience.status === 'stale' || audience.staleReason !== null);
  /** The audience as it stands can be reworded: there, and waiting on nothing above it. */
  const editable = !frozen && audience !== null && !stale;
  const guideApproved = view?.guideApproved === true;
  /** The edits still open over screeners the audience on screen holds. */
  const shownIds = new Set(audience?.content.screeners.map((screener) => screener.id) ?? []);
  const openEdits = editable ? [...edits.keys()].filter((id) => shownIds.has(id)).length : 0;
  const draftWaiting = draftPendingId !== null && !draftOverdue;
  const draftLate = draftPendingId !== null && draftOverdue;
  const draftFailed = draftForNow?.status === 'unavailable' || draftForNow?.status === 'unreadable';
  /** "Try again" is on offer over a failed, late or unaskable update the audience still needs. */
  const retryOffered =
    stale &&
    !frozen &&
    guideApproved &&
    !asking &&
    !draftWaiting &&
    (draftFailed || draftLate || requestError !== null);

  const firstDraft: FirstDraft<'guide'> | null = firstDraftOf({
    read: view !== null,
    drafted: audience !== null,
    waitsOn: guideApproved ? null : 'guide',
    failed: firstDraftId !== null && firstDraftOverdue,
  });

  /**
   * What holds the approval of the audience on screen, first what the reader can act on, or null
   * when nothing does: Continue approves on exactly that. With no audience drafted yet it names what
   * the first draft waits on, as the step's heading does.
   */
  const approvalBlock = (): string | null => {
    if (firstDraft !== null) {
      return firstDraft.status === 'needs-upstream'
        ? DG_NOT_APPROVED_REFUSAL
        : firstDraft.status === 'failed'
          ? AUDIENCE_DRAFT_FAILED_HINT
          : AUDIENCE_DRAFTING_HINT;
    }

    if (retryOffered) {
      return AUDIENCE_RETRY_HINT;
    }

    if (!guideApproved) {
      return DG_NOT_APPROVED_REFUSAL;
    }

    if (!editable || audience.status !== 'unapproved' || asking) {
      return AUDIENCE_UPDATING_HINT;
    }

    if (openEdits > 0) {
      return EDITS_OPEN_HINT;
    }

    return approving || saving !== null || settling ? SAVING_HINT : null;
  };
  const blocked = approvalBlock();
  const approvable = blocked === null;

  const { refusal, continueRun } = useRefusalFocus();
  /**
   * Continue approves an audience not yet approved, and stays on the step over a refusal, which it
   * takes the reader to. Before one is drafted it is held with what the draft waits on. An approved
   * or fixed audience leaves Continue a plain move, and so does the first read still out.
   */
  useContinueAction(
    view === null || frozen || audience?.status === 'approved'
      ? null
      : {
          run: continueRun(approve),
          disabled: !approvable,
          blockedHint: blocked,
          busyLabel: APPROVING_LABEL,
        },
  );

  if (view === null) {
    return loadError === null ? (
      <p data-test-id="audience-loading">Reading your audience…</p>
    ) : (
      <p className="chat-error" data-test-id="audience-error" role="alert">
        {loadError}{' '}
        <button type="button" data-test-id="audience-reload" onClick={() => void load()}>
          Try again
        </button>
      </p>
    );
  }

  /** The changes above the audience it is waiting on, in the order they travel. */
  const waitingOn = stalenessWindowsOf(audience?.staleReason ?? null).map(
    (window) => window.upstream_kind,
  );
  /**
   * What the current version changed and why, while it is an updated version nobody has approved and
   * nothing above it has changed since: the marks go once it is approved or waits on a newer change,
   * and History keeps the line.
   */
  const reconciliation =
    audience !== null &&
    audience.status === 'unapproved' &&
    view.reconciliation?.version === audience.version
      ? view.reconciliation
      : null;
  const marks = new Map((reconciliation?.items ?? []).map((item) => [item.id, item]));
  const removed = (reconciliation?.items ?? []).filter((item) => item.change === 'removed');
  const fieldChanged = (field: string) => reconciliation?.changedFields?.includes(field) === true;
  const reasonShown =
    audience === null
      ? ''
      : (view.history.find((entry) => entry.version === audience.version)?.reasonText ?? '');
  const recommended = `Sigora recommends ${String(view.recommended.completes)}${
    studyTypeName === null ? '' : ` for ${studyTypeName}`
  }. ${view.recommended.rationale} It never goes below ${String(view.floor)}, the quality floor.`;

  const renderEdit = (screener: AudienceScreener, edit: ScreenerEdit) => {
    const error = editErrors.get(screener.id) ?? null;
    const fieldId = `audience-wording-${screener.id}`;
    const saveOnEnter = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
        event.preventDefault();
        save(screener);
      }
    };

    return (
      <div
        className="guide-edit"
        data-test-id="audience-screener-editor"
        onKeyDown={(event) => {
          if (event.key === 'Escape' && saving !== screener.id) {
            event.preventDefault();
            closeEdit(screener.id);
          }
        }}
      >
        <label className="visually-hidden" htmlFor={fieldId}>
          Question wording
        </label>
        <textarea
          id={fieldId}
          data-test-id="audience-screener-wording"
          value={edit.text}
          maxLength={MAX_SCREENER_TEXT_CHARS}
          rows={2}
          disabled={saving === screener.id}
          ref={refInto(editFields.current, screener.id)}
          onChange={(event) => {
            const text = event.target.value;
            typeInto(screener.id, (open) => ({ ...open, text }));
          }}
          onKeyDown={saveOnEnter}
        />
        <ol className="guide-edit-options">
          {edit.options.map((option, index) => {
            const optionId = `audience-option-${screener.id}-${option.id}`;

            return (
              <li key={option.id}>
                <label className="visually-hidden" htmlFor={optionId}>
                  Answer {index + 1}
                </label>
                <input
                  id={optionId}
                  type="text"
                  data-test-id="audience-screener-option-wording"
                  data-option-id={option.id}
                  value={option.text}
                  maxLength={MAX_SCREENER_OPTION_CHARS}
                  disabled={saving === screener.id}
                  onChange={(event) => {
                    const text = event.target.value;
                    typeInto(screener.id, (open) => ({
                      ...open,
                      options: open.options.map((typed) =>
                        typed.id === option.id ? { ...typed, text } : typed,
                      ),
                    }));
                  }}
                  onKeyDown={saveOnEnter}
                />
              </li>
            );
          })}
        </ol>
        <div className="guide-edit-actions">
          <button
            type="button"
            data-test-id="audience-screener-save"
            disabled={busy || !savable(edit)}
            onClick={() => save(screener)}
          >
            {saving === screener.id ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            data-test-id="audience-screener-cancel"
            disabled={saving === screener.id}
            onClick={() => closeEdit(screener.id)}
          >
            Cancel
          </button>
        </div>
        {error !== null && (
          <p className="chat-error" data-test-id="audience-screener-edit-error" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="audience" data-test-id="audience">
      {audience === null ? (
        <h2 className="guide-heading" data-test-id="audience-waiting">
          {firstDraft?.status === 'failed' ? (
            'Sigora has not drafted the audience from your guide. Ask Sigora below to draft it.'
          ) : view.guideApproved ? (
            <>
              <PendingMark />
              Sigora is drafting the audience from your guide; it appears here when it is ready.
            </>
          ) : (
            'Sigora drafts the audience once you approve your guide.'
          )}
        </h2>
      ) : (
        <h2 className="guide-heading" data-test-id="audience-hero">
          Sigora will find {audience.content.target_completes} people like this.
          <FieldMark shown={fieldChanged('target_completes')} explained={reasonShown} />
        </h2>
      )}

      {notice !== null && (
        <p
          ref={refusal}
          className="guide-notice"
          data-test-id="audience-notice"
          role="status"
          tabIndex={-1}
        >
          {notice}
        </p>
      )}

      {loadError !== null && (
        <p className="chat-error" data-test-id="audience-refresh-error" role="alert">
          Your audience could not be read again, so this may be out of date. {loadError}{' '}
          <button type="button" data-test-id="audience-refresh-retry" onClick={() => void load()}>
            Try again
          </button>
        </p>
      )}

      {audience !== null && (
        <>
          <p className="guide-status" data-test-id="audience-recommended">
            {recommended}
          </p>

          {stale && !frozen && (
            <div aria-live="polite">
              {!view.guideApproved && (
                <p className="guide-notice" data-test-id="audience-reconcile-needs-guide">
                  {AUDIENCE_NEEDS_GUIDE}
                </p>
              )}
              {view.guideApproved && (asking || draftWaiting) && (
                <Pending live={false} className="guide-notice" testId="audience-reconcile-pending">
                  {audienceUpdatingNotice(waitingOn)}
                </Pending>
              )}
              {view.guideApproved && draftLate && (
                <p className="guide-notice" data-test-id="audience-reconcile-overdue">
                  {AUDIENCE_UPDATE_OVERDUE}
                </p>
              )}
              {view.guideApproved && draftFailed && !asking && (
                <p className="guide-notice" data-test-id="audience-reconcile-failed">
                  {AUDIENCE_UPDATE_FAILED}
                </p>
              )}
              {retryOffered && (
                <button
                  type="button"
                  data-test-id="audience-retry"
                  disabled={busy}
                  onClick={() => askDraft(true)}
                >
                  Try again
                </button>
              )}
              {requestError !== null && (
                <p className="chat-error" data-test-id="audience-request-error" role="alert">
                  {requestError}
                </p>
              )}
              <p className="guide-status" data-test-id="audience-before-change">
                {AUDIENCE_BEFORE_CHANGE}
              </p>
            </div>
          )}

          {reconciliation !== null && (
            <div className="guide-notice" data-test-id="audience-reconciled" role="status">
              <p>
                {reconciliation.unchanged
                  ? audienceUnchangedNotice(reconciliation.upstreamKinds)
                  : audienceUpdatedNotice(reconciliation.upstreamKinds)}
              </p>
              {removed.length > 0 && (
                <>
                  <p>{AUDIENCE_REMOVED_HEADING}</p>
                  <ul data-test-id="audience-reconciled-removed">
                    {removed.map((item) => {
                      const reason = item.reason ?? reasonShown;
                      return (
                        <li key={item.id} data-test-id="audience-removed-item">
                          {item.label}
                          {reason === '' ? '' : ` — ${reason}`}
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
            </div>
          )}

          <dl className="audience-rows">
            <div className="audience-row" data-test-id="audience-row-who">
              <dt>Who</dt>
              <dd>
                {audience.content.description}
                <FieldMark shown={fieldChanged('description')} explained={reasonShown} />
              </dd>
            </div>
            <div className="audience-row" data-test-id="audience-row-where">
              <dt>Where</dt>
              <dd>
                {countryNamesInReadingOrder(audience.content.countries).join(' and ')}
                <FieldMark shown={fieldChanged('countries')} explained={reasonShown} />
              </dd>
            </div>
            <div className="audience-row" data-test-id="audience-row-language">
              <dt>Language</dt>
              <dd>{AUDIENCE_LANGUAGE}</dd>
            </div>
            <div className="audience-row" data-test-id="audience-row-filters">
              <dt>Filters</dt>
              <dd>
                {audience.content.filters.length === 0 ? (
                  'None'
                ) : (
                  <ul className="audience-filters">
                    {audience.content.filters.map((filter) => {
                      const labelled = labelAudienceFilter(filter, audienceCatalog);

                      return (
                        <li key={filter.id} data-test-id="audience-filter">
                          <AudienceValue label={labelled.dimension} />
                          {labelled.range ? ' ' : ': '}
                          {labelled.options.map((option, index) => (
                            // Position: the same option can appear twice in one document.
                            <Fragment key={index}>
                              {index > 0 && ', '}
                              <AudienceValue label={option} />
                            </Fragment>
                          ))}
                          {filter.mode === 'capture_only' && (
                            <span
                              className="audience-capture"
                              data-test-id={`audience-capture-${filter.id}`}
                            >
                              Collected for analysis, not used to choose who takes part
                            </span>
                          )}
                          <ChangeMark item={marks.get(filter.id)} explained={reasonShown} />
                        </li>
                      );
                    })}
                  </ul>
                )}
              </dd>
            </div>
          </dl>

          {audience.content.screeners.length > 0 && (
            <section className="audience-screeners" aria-labelledby="audience-screeners-heading">
              <h3 id="audience-screeners-heading">Screening questions</h3>
              <ol className="guide-questions">
                {audience.content.screeners.map((screener, index) => {
                  const edit = editable ? edits.get(screener.id) : undefined;

                  return (
                    <li
                      className="guide-question"
                      data-test-id="audience-screener"
                      data-screener-id={screener.id}
                      key={screener.id}
                    >
                      {edit === undefined ? (
                        <>
                          <p className="guide-question-text" data-test-id="audience-screener-text">
                            {screener.text}
                          </p>
                          <ul className="guide-question-options">
                            {screener.options.map((option) => (
                              <li
                                key={option.id}
                                data-test-id="audience-screener-option"
                                data-option-id={option.id}
                              >
                                {option.text}
                                {option.terminates && (
                                  <>
                                    {' '}
                                    <span
                                      className="audience-ends"
                                      data-test-id="audience-screener-ends"
                                    >
                                      Ends the survey
                                    </span>
                                  </>
                                )}
                              </li>
                            ))}
                          </ul>
                        </>
                      ) : (
                        renderEdit(screener, edit)
                      )}

                      {screener.worded_by === 'user' && (
                        <span className="guide-tag" data-test-id="audience-your-wording">
                          Your wording
                        </span>
                      )}

                      <ChangeMark item={marks.get(screener.id)} explained={reasonShown} />

                      {editable && edit === undefined && (
                        <button
                          type="button"
                          className="guide-edit-open text-link muted"
                          data-test-id="audience-screener-edit"
                          aria-label={`Edit screening question ${String(index + 1)}`}
                          ref={refInto(editButtons.current, screener.id)}
                          onClick={() => openEdit(screener)}
                        >
                          Edit
                        </button>
                      )}
                    </li>
                  );
                })}
              </ol>
            </section>
          )}

          <Limitations content={audience.content} marks={marks} explained={reasonShown} />

          <p className="guide-status" data-test-id="audience-status">
            {STATUS_LABELS[audience.status]}
          </p>

          {editable && audience.status === 'unapproved' && !view.guideApproved && (
            <p className="guide-status" data-test-id="audience-needs-guide">
              {DG_NOT_APPROVED_REFUSAL}
            </p>
          )}

          {approveError !== null && (
            <p
              ref={refusal}
              className="chat-error"
              data-test-id="audience-approve-error"
              role="alert"
              tabIndex={-1}
            >
              {approveError}
            </p>
          )}

          <VersionHistory
            testIdPrefix="audience"
            current={{ version: audience.version, reasonText: reasonShown }}
            entries={historyEntriesOf(view.history)}
          />
        </>
      )}
    </div>
  );
};
